import React, { useState, useEffect, useCallback } from 'react';
import './StatusTab.css';

const fmtUptime = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}min`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

// Tamanho legível: KB/MB abaixo de 1 GB, GB a partir daí.
const fmtBytes = (b) => {
  const n = Number(b) || 0;
  const nf = (v, d) => v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
  if (n >= 1024 ** 3) return `${nf(n / 1024 ** 3, 2)} GB`;
  if (n >= 1024 ** 2) return `${nf(n / 1024 ** 2, 1)} MB`;
  return `${nf(n / 1024, 0)} KB`;
};

const Bar = ({ pct, warn }) => (
  <div className="st-bar">
    <div
      className={`st-bar-fill ${pct >= (warn || 85) ? 'hot' : pct >= 60 ? 'warm' : ''}`}
      style={{ width: `${Math.min(100, pct || 0)}%` }}
    />
  </div>
);

function StatusTab({ svc }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getStatus();
      if (d.error) throw new Error(d.error);
      setData(d);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="st-view"><p className="st-msg">Verificando sistemas…</p></div>;

  const r = data?.resources || {};
  const b = data?.backend || {};

  return (
    <div className="st-view">
      <div className="st-head">
        <span className="st-updated">{data?.checked_at ? `Verificado às ${data.checked_at}` : ''}</span>
        <button className="st-refresh" onClick={load} disabled={loading}>{loading ? '…' : '🔄 Atualizar'}</button>
      </div>
      {error && <p className="st-msg st-err">{error}</p>}

      {/* Containers Docker */}
      <div className="st-card">
        <h3 className="st-title">🐳 Containers Docker</h3>
        {data?.containers_error && <p className="st-sub st-err">{data.containers_error}</p>}
        {(data?.containers || []).length === 0 && !data?.containers_error && (
          <p className="st-sub">Nenhum container visível.</p>
        )}
        {(data?.containers || []).map((c) => (
          <div className="st-row" key={c.name}>
            <span className={`st-dot ${c.state === 'running' ? 'on' : 'off'}`} />
            <span className="st-row-name">{c.name}</span>
            <span className="st-row-meta">{c.status}</span>
          </div>
        ))}
      </div>

      {/* Recursos da VM */}
      <div className="st-card">
        <h3 className="st-title">🖥️ Recursos da VM (Oracle)</h3>
        <div className="st-metric">
          <div className="st-metric-head">
            <span>Memória RAM</span>
            <span>{r.ram_used_mb != null ? `${r.ram_used_mb} / ${r.ram_total_mb} MB` : '—'} ({r.ram_pct ?? '—'}%)</span>
          </div>
          <Bar pct={r.ram_pct} warn={85} />
        </div>
        <div className="st-metric">
          <div className="st-metric-head">
            <span>CPU {r.cpus ? `(${r.cpus} vCPU)` : ''}</span>
            <span>{r.cpu_pct ?? '—'}% {r.load ? `· load ${r.load[0].toFixed(2)}` : ''}</span>
          </div>
          <Bar pct={r.cpu_pct} warn={85} />
        </div>
        <div className="st-metric">
          <div className="st-metric-head">
            <span>Disco</span>
            <span>{r.disk_used_gb != null ? `${r.disk_used_gb} / ${r.disk_total_gb} GB` : '—'} ({r.disk_pct ?? '—'}%)</span>
          </div>
          <Bar pct={r.disk_pct} warn={90} />
          {r.disk_breakdown && (
            <div style={{ marginTop: 10, fontSize: 12 }}>
              {r.disk_breakdown.map((i) => (
                <div key={i.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: 'var(--text-secondary)' }}>
                  <span>{i.label}</span>
                  <span>
                    {fmtBytes(i.bytes)}
                    {i.recuperavel_bytes > 0 && ` (${fmtBytes(i.recuperavel_bytes)} liberável)`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Backend + HTTPS */}
      <div className="st-card">
        <h3 className="st-title">⚙️ Backend & HTTPS</h3>
        <div className="st-row">
          <span className="st-dot on" />
          <span className="st-row-name">Backend no ar</span>
          <span className="st-row-meta">uptime {fmtUptime(b.uptime_seconds)}</span>
        </div>
        <div className="st-row">
          <span className={`st-dot ${b.https_error ? 'off' : 'on'}`} />
          <span className="st-row-name">Certificado HTTPS</span>
          <span className="st-row-meta">
            {b.https_error ? 'erro' : b.https_valid_until
              ? `válido até ${b.https_valid_until} (${b.https_days_left}d)` : '—'}
          </span>
        </div>
      </div>

      {/* Consumo de IA (Claude) */}
      {data?.ai_usage && !data.ai_usage.error && (
        <div className="st-card">
          <h3 className="st-title">🧠 IA (Claude) — tokens e custo</h3>
          <div className="st-row">
            <span className="st-row-name">Hoje</span>
            <span className="st-row-meta">
              {data.ai_usage.hoje.calls} análise(s) · {data.ai_usage.hoje.input_tokens + data.ai_usage.hoje.output_tokens} tokens · US$ {data.ai_usage.hoje.cost_usd.toFixed(4)}
            </span>
          </div>
          <div className="st-row">
            <span className="st-row-name">Últimos 30 dias</span>
            <span className="st-row-meta">
              {data.ai_usage.ultimos_30_dias.calls} análise(s) · {data.ai_usage.ultimos_30_dias.input_tokens + data.ai_usage.ultimos_30_dias.output_tokens} tokens · US$ {data.ai_usage.ultimos_30_dias.cost_usd.toFixed(4)}
            </span>
          </div>
          <div className="st-row">
            <span className="st-row-name">Total acumulado</span>
            <span className="st-row-meta">
              {data.ai_usage.total.calls} análise(s) · {data.ai_usage.total.input_tokens + data.ai_usage.total.output_tokens} tokens · US$ {data.ai_usage.total.cost_usd.toFixed(4)}
            </span>
          </div>
          <p className="st-sub">
            Saldo de crédito da conta não é exposto pela API do Claude — confira em{' '}
            <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noreferrer">
              console.anthropic.com/settings/billing
            </a>.
          </p>
        </div>
      )}

      {/* Integrações externas */}
      <div className="st-card">
        <h3 className="st-title">🔌 Integrações externas</h3>
        {(data?.integrations || []).map((i) => (
          <div className="st-row" key={i.name}>
            <span className={`st-dot ${i.ok ? 'on' : 'off'}`} />
            <span className="st-row-name">{i.name}</span>
            <span className="st-row-meta">{i.ok ? `${i.ms} ms` : 'sem resposta'}</span>
          </div>
        ))}
      </div>
      <div className="st-spacer" />
    </div>
  );
}

export default StatusTab;
